import { Component, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core'
import { AbstractControl, FormControl, Validators } from '@angular/forms'
import { MatExpansionPanel } from '@angular/material/expansion'
import { CURRENT_ACCOUNT_ID, Channel, SaveForm, Destroyable } from '@tokeet-frontend/tv3-platform'
import { BehaviorSubject } from 'rxjs'
import { ChannelConnectHelperService, connectChannelErrorReason } from '../../channel-connect-helper.service'

interface ConnectError {
  reason: string
  message: string
  retryable: boolean
}

@Component({
  selector: 'app-connect-bdc-wizard-step3',
  templateUrl: './connect-bdc-wizard-step3.component.html',
  styleUrls: ['./connect-bdc-wizard-step3.component.scss'],
  providers: [ChannelConnectHelperService],
})
export class ConnectBDCWizardStep3Component extends Destroyable {
  @ViewChild('settings') startStep: MatExpansionPanel
  @Input() channel: Channel

  @Output() prev = new EventEmitter()
  @Output() connected = new EventEmitter<string>()

  isConnecting = false
  connectError: ConnectError | null = null
  propertyIdCtrl = new FormControl('', [Validators.required, Validators.pattern(/^\d+$/)])

  constructor(
    private channelHelperService: ChannelConnectHelperService,
    @Inject(CURRENT_ACCOUNT_ID) public accountId$: BehaviorSubject<number>
  ) {
    super()
  }

  @SaveForm()
  onConnect(form: AbstractControl) {
    const propertyId = this.propertyIdCtrl.value
    this.isConnecting = true
    this.connectError = null
    this.channelHelperService.connectChannelWithPropertyId(this.channel, propertyId).subscribe({
      next: () => {
        this.isConnecting = false
        this.connected.emit(propertyId)
      },
      error: (error) => {
        this.isConnecting = false
        this.connectError = this.toConnectError(error)
      },
    })
  }

  reset() {
    this.propertyIdCtrl.reset()
    this.startStep.open()
    this.isConnecting = false
    this.connectError = null
  }

  private toConnectError(error: any): ConnectError {
    const reason = connectChannelErrorReason(error)
    switch (reason) {
      case 'bdc_access_denied':
        return {
          reason,
          retryable: true,
          message: $localize`We couldn't verify this property with Booking.com. Please check that the Property ID is correct and that you completed the Channel Manager step in the Booking.com extranet (steps 1 and 2 above), then try again.`,
        }
      case 'bdc_request_pending':
        return {
          reason,
          retryable: true,
          message: $localize`Your Booking.com connection request is still pending approval. Please try again in a few minutes.`,
        }
      case 'property_already_connected':
        return {
          reason,
          retryable: false,
          message: $localize`This property is already connected to your account. You can manage it from the Connections list.`,
        }
      case 'property_connected_other_account':
        return {
          reason,
          retryable: false,
          message: $localize`This property is already connected to a different account. Please disconnect it there first or contact support.`,
        }
      case 'bdc_unavailable':
      case 'bdc_auth_unavailable':
      case 'channel_unavailable':
        return {
          reason,
          retryable: true,
          message: $localize`Booking.com is temporarily unavailable. This is not a problem with your setup — please try again in a few minutes.`,
        }
      default:
        return {
          reason,
          retryable: true,
          message:
            error?.error?.error ||
            $localize`We couldn't connect this property. Please try again, and contact support if the problem persists.`,
        }
    }
  }
}
